import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import {
  Box, Button, Chip,
  Card, CardActions, CardContent, CardMedia, Container,
  Divider, Grid,
  List, ListItem, ListItemIcon, ListItemText, 
  Typography
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { Content, InfoCard, ItemCardHeader, Link } from '@backstage/core-components';
import { NewAnnouncementBanner } from '@backstage-community/plugin-announcements';

import MenuBookIcon from '@material-ui/icons/MenuBook';
import MapIcon from '@material-ui/icons/Map';
import PolicyIcon from '@material-ui/icons/Policy';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import TeamsIcon from '@material-ui/icons/Chat';
import TicketIcon from '@material-ui/icons/ConfirmationNumber';

const useStyles = makeStyles<
  Theme,
  {
    startWithOnePlatformFontColor: string;
    startWithOnePlatformBackgroundImage: string;
    legacyAndBusinessAsUsualFontColor: string;
    legacyAndBusinessAsUsualBackgroundImage: string;
  }
>(( theme ) => ({
  box: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 10,
    '-webkit-box-orient': 'vertical',
  },
  startWithOnePlatform: {
    color: ({ startWithOnePlatformFontColor }) => startWithOnePlatformFontColor,
    backgroundImage: ({ startWithOnePlatformBackgroundImage }) => startWithOnePlatformBackgroundImage,
  },
  legacyAndBusinessAsUsual: {
    color: ({ legacyAndBusinessAsUsualFontColor }) => legacyAndBusinessAsUsualFontColor,
    backgroundImage: ({ legacyAndBusinessAsUsualBackgroundImage }) => legacyAndBusinessAsUsualBackgroundImage,
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  operationalText: {
    color: theme.palette.success.main,
    display: 'flex',
    alignItems: 'center',
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
  },
  statusIcon: {
    marginRight: theme.spacing(1),
  },
  chipOperational: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
}));

export const HomePage = () => {
  const navigate = useNavigate();

  const { getPageTheme } = useTheme();
  const startWithOnePlatformPageTheme = getPageTheme({ themeId: 'home' });
  const legacyAndBusinessAsUsualPageTheme = getPageTheme({ themeId: 'other' });
  const classes = useStyles({
    startWithOnePlatformFontColor: startWithOnePlatformPageTheme.fontColor,
    startWithOnePlatformBackgroundImage: startWithOnePlatformPageTheme.backgroundImage,
    legacyAndBusinessAsUsualFontColor: legacyAndBusinessAsUsualPageTheme.fontColor,
    legacyAndBusinessAsUsualBackgroundImage: legacyAndBusinessAsUsualPageTheme.backgroundImage,
  });

  const [suggestedCloudProvider] = useState<'aws' | 'azure'>('azure');

  // Le sezioni "What's New" e "Platform Health & Status" fanno parte del piano
  // di redesign (vedi docs/portal-redesign) ma non sono ancora pronte per essere
  // popolate con contenuti reali: le teniamo nascoste finché i tempi non saranno maturi.
  // Impostare a `true` per riattivarle.
  const showUpcomingSections = false;

  return (
    <Content noPadding>
      <Container style={{ marginLeft: 'auto', marginRight: 'auto', maxWidth: '97%' }}>
        <Grid container alignItems="flex-end">
          <Grid item xs={12}>
            <NewAnnouncementBanner max={3} />
          </Grid>
          <Grid item xs={7}>
            <Typography variant="h2">Welcome to TeamSystem Development Portal</Typography>
            <Typography variant="h4">
              Building the future of business software, together
            </Typography>
            <Typography variant="body1">
              Your gateway to OnePlatform - the foundation for modern, scalable, and secure business applications.
            </Typography>
          </Grid>
          <Grid item xs={5}>
            <Alert severity="info" icon={false} style={{ marginBottom: 10 }}>
              <Grid container direction="row" justifyContent="center" alignItems="center">
                <Grid item xs={10}>
                  <b>Cloud Provider Suggestion</b>
                  <br />
                  For your new Cloud project or service, we strongly
                  recommend to use
                </Grid>
                <Grid item xs={2}>
                  <img
                    style={{ width: '70%', marginTop: 10 }}
                    src={`resources/images/${
                      suggestedCloudProvider === 'aws'
                        ? 'awsLogo.png'
                        : 'msAzureLogo.png'
                    }`}
                    alt={`${
                      suggestedCloudProvider === 'aws' ? 'AWS' : 'Azure'
                    }`}
                  />
                </Grid>
              </Grid>
            </Alert>
          </Grid>
        </Grid>

        <Divider style={{ marginTop: 10, marginBottom: 20 }} />

        <Typography variant="h4">Two Paths Forward</Typography>

        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={6}>
            <Card>
              <CardMedia>
                <ItemCardHeader
                  title="🚀 Start with OnePlatform (Recommended)"
                  subtitle="The Golden Path for New Projects"
                  classes={{ root: classes.startWithOnePlatform }}
                />
              </CardMedia>
              <CardContent>
                <Box className={classes.box}>
                  <Typography variant="body2" style={{ minHeight: '50px' }}>
                    Build modern, cloud-native applications using our integrated platform services. Get started in minutes with pre-built capabilities, automated DevOps, and enterprise-grade security.
                  </Typography>
                  <Typography variant="body2">
                    <b>Perfect for: </b>New product development, Greenfield projects & Modernization initiatives
                  </Typography>
                </Box>
              </CardContent>
              <CardActions>
                <Button
                  color="primary"
                  onClick={() => {
                    navigate('/guide/oneplatform-onboarding');
                  }}
                >
                  Begin Your Journey →
                </Button>
              </CardActions>
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card>
              <CardMedia>
                <ItemCardHeader
                  title="🔧 Legacy & Business as Usual"
                  subtitle="Working with Existing Systems"
                  classes={{ root: classes.legacyAndBusinessAsUsual }}
                />
              </CardMedia>
              <CardContent>
                <Box className={classes.box}>
                  <Typography variant="body2" style={{ minHeight: '50px' }}>
                    Access documentation, APIs, and support for existing products and services. Find integration patterns and migration paths.
                  </Typography>
                  <Typography variant="body2">
                    <b>Use this for: </b>Maintaining existing products, Legacy system integration & Migration planning
                  </Typography>
                </Box>
              </CardContent>
              <CardActions>
                {/* Reindirizzato all'indice delle guide (/guide) finché la pagina
                    dedicata /guide/legacy-resources non avrà contenuti reali. */}
                <Button
                  color="primary"
                  onClick={() => {
                    navigate('/guide');
                  }}
                >
                  Access Legacy Resources →
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>

        <Divider style={{ marginTop: 10, marginBottom: 20 }} />

        <Grid container spacing={2} alignItems="stretch">

          {/* QUADRANT 1: Featured Resources */}
          <Grid item xs={12} md={6}>
            <InfoCard title="Featured Resources" className={classes.card}>
              <List>
                <ListItem button component={Link} to="/guide/oneplatform-getting-started">
                  <ListItemIcon><MenuBookIcon /></ListItemIcon>
                  <ListItemText primary="Getting Started with OnePlatform" secondary="Comprehensive guides, tutorials, and API references." />
                </ListItem>
                <ListItem button component={Link} to="/guide/citymap-guided-tour">
                  <ListItemIcon><MapIcon /></ListItemIcon>
                  <ListItemText primary="Interactive Citymap" secondary="Explore architecture layers from infrastructure to business." />
                </ListItem>
                <ListItem button component={Link} to="/guide/manifesto">
                  <ListItemIcon><PolicyIcon /></ListItemIcon>
                  <ListItemText primary="Platform Manifesto" secondary="Modular, Consistent, Self-Service, Future-proof." />
                </ListItem>
              </List>
            </InfoCard>
          </Grid>

          {/* QUADRANT 2: What's New (nascosto: in attesa di contenuti reali) */}
          {showUpcomingSections && (
            <Grid item xs={12} md={6}>
              <InfoCard
                title="What's New"
                className={classes.card}
                // Adds a link in the top right of the card
                action={<Button color="primary" component={Link} to="/news">View All Updates</Button>}
              >
                <List>
                  <ListItem>
                    <ListItemText primary={<><strong>TeamSystem-ID v2.3</strong></>} secondary="Enhanced OAuth 2.0 flows and MFA support" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary={<><strong>API Gateway</strong></>} secondary="New rate limiting and monitoring dashboards" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary={<><strong>Hermes (Data Streaming)</strong></>} secondary="Kafka cluster expansion and new connectors" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary={<><strong>AI Platform</strong></>} secondary="New LLM models and RAG capabilities" />
                  </ListItem>
                </List>
              </InfoCard>
            </Grid>
          )}

          {/* QUADRANT 3: Platform Health & Status (nascosto: in attesa di contenuti reali) */}
          {showUpcomingSections && (
            <Grid item xs={12} md={6}>
              <InfoCard
                title="Platform Health & Status"
                className={classes.card}
                action={<Button color="primary" component={Link} to="/status">View Status Page</Button>}
              >
                <Box mb={2}>
                  <Typography variant="h6" className={classes.operationalText}>
                    <CheckCircleIcon className={classes.statusIcon} /> All Systems Operational
                  </Typography>
                </Box>

                <List>
                  <ListItem>
                    <ListItemText primary="Infrastructure" />
                    <Chip label="Operational" size="small" className={classes.chipOperational} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Core Services" />
                    <Chip label="Operational" size="small" className={classes.chipOperational} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Business Capabilities" />
                    <Chip label="Operational" size="small" className={classes.chipOperational} />
                  </ListItem>
                </List>
              </InfoCard>
            </Grid>
          )}

          {/* QUADRANT 4: Get Help */}
          <Grid item xs={12} md={6}>
            <InfoCard title="Get Help" className={classes.card}>
              <List>
                <ListItem button component={Link} to="/docs">
                  <ListItemIcon><MenuBookIcon /></ListItemIcon>
                  <ListItemText primary="Documentation on Development Portal" secondary="Guides, references, and technical docs for the portal." />
                </ListItem>
                <ListItem button component={Link} to="https://teams.microsoft.com/l/team/19%3A9df10083c04d41d9a28d4878c42b3c5f%40thread.tacv2/conversations?groupId=36e5c8c0-c9d4-4eb7-bc48-96c68151579c&tenantId=bb364f09-07cf-4eca-9b92-1f26a92d5f3f">
                  <ListItemIcon><TeamsIcon /></ListItemIcon>
                  <ListItemText primary="Teams: #G365-OneP Platform Product" secondary="Ask questions and chat with the platform team." />
                </ListItem>
                <ListItem button component={Link} to="https://teamsystem.atlassian.net/servicedesk/customer/portals">
                  <ListItemIcon><TicketIcon /></ListItemIcon>
                  <ListItemText primary="Create Support Ticket" secondary="Open a request on the service desk for dedicated support." />
                </ListItem>
              </List>
            </InfoCard>
          </Grid>

        </Grid>
      </Container>
    </Content>
  );
};
